import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import { CancelSaveButtons } from '../../../components/common';
import WebPageLayout from '../../../components/layout/WebPageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSModal from '../../../components/designSystem/DSModal';
import RichTextEditor, { type RichTextEditorHandle } from '../../../components/common/RichTextEditor';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import { uploadProjectImage } from '../../../Module/Project/Presentation/projectActions';
import infoIconImg from '../../../assets/Icon/project/info_icon.png';
import searchIcon from '../../../assets/Icon/project/setting/search_icon.png';
import searchActiveIcon from '../../../assets/Icon/project/setting/search_active_icon.png';
import penIcon from '../../../assets/Icon/project/setting/pen_icon.png';
import penActiveIcon from '../../../assets/Icon/project/setting/pen_active_icon.png';
import checkRoundedIcon from '../../../assets/Icon/project/check-rounded.png';
import pictureIconImg from '../../../assets/Icon/project/picture_icon.png';
import { FOOTER_BREAKPOINT, MOBILE_BREAKPOINT, convertToVwMob } from '../../../Utils/responsive';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getProject, updateProject } from '../../../Module/Project/Presentation/projectActions';
import { selectGetProjectStatus, selectUpdateProjectStatus, selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { theme } from '../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { extractDigits, formatCurrencyInput, formatKoreanCurrency, parseCurrencyNumber } from '../../../Utils/formatCurrency';
import { hasLegacyStoryMarkup, prepareStoryHtmlForEditor, restoreStoryHtmlFromEditor, sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import { getCloudFrontImageUrl, THUMBNAIL_DIMENSIONS } from '../../../Utils/cloudFrontImage';
import { isImageSizeAllowed } from '../../../Utils/imageUpload';
import { PATHS } from '../../../routes/paths';

type TabKey = 'basic' | 'story';

const STORY_IMAGE_LIMIT = 3;

const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

const isValidHttpUrl = (raw: string) => {
  if (!raw.trim()) return false;
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const ProjectSettingsWeb = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation');

  const getStatus = useAppSelector(selectGetProjectStatus);
  const updateStatus = useAppSelector(selectUpdateProjectStatus);
  const project = useAppSelector(selectSelectedProject);

  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [currency, setCurrency] = useState<'KRW' | 'USD'>('KRW');
  const [goalAmountKrw, setGoalAmountKrw] = useState<string>('');
  const [goalAmountUsd, setGoalAmountUsd] = useState<string>('');
  const goalAmount = currency === 'KRW' ? goalAmountKrw : goalAmountUsd;
  const setGoalAmount = currency === 'KRW' ? setGoalAmountKrw : setGoalAmountUsd;
  const [title, setTitle] = useState<string>('');
  const [projectDesc, setProjectDesc] = useState<string>('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');

  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [isLegacyStoryEditorMode, setIsLegacyStoryEditorMode] = useState(false);
  const [exitConfirmTarget, setExitConfirmTarget] = useState<'media' | null>(null);
  const [tempMediaType, setTempMediaType] = useState<'image' | 'video' | 'none'>('image');
  const [tempMediaFile, setTempMediaFile] = useState<File | null>(null);
  const [tempMediaPreview, setTempMediaPreview] = useState<string | null>(null);
  const [tempVideoUrl, setTempVideoUrl] = useState<string>('');
  const [tempVideoUrlFocused, setTempVideoUrlFocused] = useState(false);
  const [tempVideoPreviewEnabled, setTempVideoPreviewEnabled] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const storyEditorRef = useRef<RichTextEditorHandle>(null);
  const storyEditorScrollRef = useRef<HTMLDivElement | null>(null);
  const legacyStoryEditorRef = useRef<HTMLTextAreaElement | null>(null);

  const scrollStoryEditorIntoView = useCallback(() => {
    const scrollEl = storyEditorScrollRef.current;
    if (!scrollEl) return;
    const editorEl = scrollEl.querySelector<HTMLElement>('[contenteditable="true"]');
    if (!editorEl) return;

    const pad = 48;
    const scrollRect = scrollEl.getBoundingClientRect();
    const editorRect = editorEl.getBoundingClientRect();

    const sel = window.getSelection();
    let focusTop = editorRect.top;
    let focusBottom = editorRect.bottom;
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0).getBoundingClientRect();
      if (r.width > 0 || r.height > 0) {
        focusTop = r.top;
        focusBottom = r.bottom;
      }
    }

    if (focusBottom > scrollRect.bottom - pad) {
      scrollEl.scrollTop += focusBottom - (scrollRect.bottom - pad);
    } else if (focusTop < scrollRect.top + pad) {
      scrollEl.scrollTop += focusTop - (scrollRect.top + pad);
    }
  }, []);

  const focusLegacyStoryEditor = useCallback(() => {
    const editor = legacyStoryEditorRef.current;
    if (!editor) return;
    editor.focus();
    const end = editor.value.length;
    try {
      editor.setSelectionRange(end, end);
    } catch {
      // ignore selection failures on unsupported inputs
    }
  }, []);

  const openFilePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      picker.call(input);
      return;
    }
    input.click();
  };

  const isLoading = getStatus === 'loading' && (!project || project.project_id !== projectId);
  const isSaving = updateStatus === 'loading';

  const headerTitle = useMemo(() => {
    const trimmedTitle = sanitizeProjectTitle(project?.title ?? title ?? '').trim();
    return trimmedTitle || t('page.projectSettings.placeholders.projectTitlePreview');
  }, [project?.title, title, t]);

  useEffect(() => {
    if (!projectId) {
      navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
      return;
    }
    if (getStatus === 'loading' || project?.project_id === projectId) return;
    dispatch(getProject({ project_id: projectId }));
  }, [dispatch, projectId, getStatus, project?.project_id, navigate]);

  useEffect(() => {
    if (!project) return;
    setCurrency((project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW');
    const projectCurrency = (project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    const formatted = project.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(prev => (projectCurrency === 'KRW' ? formatted : prev));
    setGoalAmountUsd(prev => (projectCurrency === 'USD' ? formatted : prev));
    setTitle(sanitizeProjectTitle(project.title ?? ''));
    setProjectDesc(String(project.project_desc ?? ''));
    setMediaPreview(project.image_url ? String(project.image_url) : null);
    setVideoUrl(project.video_url ? String(project.video_url) : '');
    setIsLegacyStoryEditorMode(false);
  }, [project]);

  useEffect(() => {
    if (!isStoryModalOpen) return;
    const legacyMode = /<\s*[a-z][^>]*>/i.test(projectDesc)
      && !/data-embed\s*=\s*["']story-image["']/i.test(projectDesc);
    if (legacyMode) {
      const timeoutIds: number[] = [];
      [0, 80, 180, 320, 520, 780].forEach((delay) => {
        const tid = window.setTimeout(() => {
          focusLegacyStoryEditor();
          requestAnimationFrame(() => {
            const scrollEl = storyEditorScrollRef.current;
            const editor = legacyStoryEditorRef.current;
            if (!scrollEl || !editor) return;
            const pad = 48;
            const scrollRect = scrollEl.getBoundingClientRect();
            const editorRect = editor.getBoundingClientRect();
            if (editorRect.bottom > scrollRect.bottom - pad) {
              scrollEl.scrollTop += editorRect.bottom - (scrollRect.bottom - pad);
            } else if (editorRect.top < scrollRect.top + pad) {
              scrollEl.scrollTop += editorRect.top - (scrollRect.top + pad);
            }
          });
        }, delay);
        timeoutIds.push(tid);
      });
      return () => timeoutIds.forEach((id) => window.clearTimeout(id));
    }
    const id = requestAnimationFrame(() => {
      storyEditorRef.current?.focusAtEnd();
      requestAnimationFrame(() => {
        scrollStoryEditorIntoView();
        requestAnimationFrame(() => {
          storyEditorRef.current?.focusAtEnd();
        });
      });
    });
    const timer = window.setTimeout(() => {
      storyEditorRef.current?.focusAtEnd();
      requestAnimationFrame(() => {
        scrollStoryEditorIntoView();
        requestAnimationFrame(() => {
          storyEditorRef.current?.focusAtEnd();
        });
      });
    }, 120);
    const timer2 = window.setTimeout(() => {
      storyEditorRef.current?.focusAtEnd();
      requestAnimationFrame(() => {
        scrollStoryEditorIntoView();
      });
    }, 520);
    return () => {
      cancelAnimationFrame(id);
      window.clearTimeout(timer);
      window.clearTimeout(timer2);
    };
  }, [focusLegacyStoryEditor, isStoryModalOpen, projectDesc, scrollStoryEditorIntoView]);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const readUrlAsBase64 = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return readFileAsBase64(new File([blob], 'image.png', { type: blob.type || 'image/png' }));
  };

  const formatGoal = (raw: string, targetCurrency: 'KRW' | 'USD') => {
    return formatCurrencyInput(raw, targetCurrency);
  };

  const handleGoalAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace') return;
    const input = e.currentTarget;
    const { value, selectionStart, selectionEnd } = input;
    if (selectionStart !== selectionEnd) return;
    if (currency === 'KRW' && value.endsWith(t('common.units.won')) && selectionStart === value.length) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'KRW') : '');
    } else if (currency === 'USD' && value.startsWith('$') && selectionStart === 1) {
      e.preventDefault();
      const digits = extractDigits(value).slice(0, -1);
      setGoalAmount(digits ? formatCurrencyInput(digits, 'USD') : '');
    }
  };

  const handleCancel = () => {
    const projectCurrency = (project?.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    setCurrency(projectCurrency);
    const formatted = project?.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(projectCurrency === 'KRW' ? formatted : '');
    setGoalAmountUsd(projectCurrency === 'USD' ? formatted : '');
    setTitle(sanitizeProjectTitle(project?.title ?? ''));
    setProjectDesc(String(project?.project_desc ?? ''));
    setMediaPreview(project?.image_url ? String(project.image_url) : null);
    setVideoUrl(project?.video_url ? String(project.video_url) : '');
    setMediaFile(null);
    setIsLegacyStoryEditorMode(false);
  };

  const handleSave = async (overrideData?: { mediaFile?: File | null; projectDesc?: string; videoUrl?: string; mediaPreview?: string | null }) => {
    if (!projectId) return;

    const titleToValidate = sanitizeProjectTitle(title).trim();
    const minGoalVal = currency === 'KRW' ? 35000 : 35;
    if (titleToValidate.length < 4 || titleToValidate.length > 40 || numericGoal < minGoalVal) {
      // eslint-disable-next-line no-alert
      alert(
        titleToValidate.length < 4 || titleToValidate.length > 40
          ? t('page.projectSettings.helpers.titleLength')
          : currency === 'KRW'
            ? t('page.projectSettings.helpers.minGoal')
            : t('page.projectSettings.helpers.minGoalUSD'),
      );
      return false;
    }

    const activeEl = document.activeElement as HTMLElement | null;
    if (activeEl?.isContentEditable) {
      activeEl.blur();
      await new Promise((r) => requestAnimationFrame(r));
    }

    const partner = String(project?.partner ?? (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF');
    const numeric = parseCurrencyNumber(currency === 'KRW' ? goalAmountKrw : goalAmountUsd);

    try {
      let imageBase64: string | undefined;
      
      // overrideData에 mediaFile이 명시적으로 null이면 삭제를 의미
      const targetMediaFile = overrideData && 'mediaFile' in overrideData ? overrideData.mediaFile : mediaFile;
      const targetMediaPreview = overrideData && 'mediaPreview' in overrideData ? overrideData.mediaPreview : mediaPreview;
      
      if (targetMediaFile) {
        imageBase64 = await readFileAsBase64(targetMediaFile);
      } else {
        imageBase64 = '';
      }

      let updatedProjectDesc = overrideData?.projectDesc ?? projectDesc;
      if (isLegacyStoryEditorMode) {
        updatedProjectDesc = restoreStoryHtmlFromEditor(updatedProjectDesc);
      }
      const overrideVideoUrl = overrideData?.videoUrl;
      
      // Handle blob URLs in projectDesc
      const blobUrls = updatedProjectDesc.match(/src="blob:[^"]+"/g) || [];
      for (const match of blobUrls) {
        const url = match.slice(5, -1);
        try {
          const resBlob = await fetch(url);
          const blob = await resBlob.blob();
          const dataUrl = await readFileAsBase64(new File([blob], 'image.png', { type: blob.type }));
          const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

          const uploadRes = await dispatch(
            uploadProjectImage({
              partner,
              project_id: projectId,
              projectpost_id: '',
              language_code: 'ko',
              isuse: true,
              image: base64,
            }),
          ).unwrap();

          if (uploadRes.image_url) {
            updatedProjectDesc = updatedProjectDesc.replace(url, uploadRes.image_url);
          }
        } catch (uploadErr) {
          console.error('Embedded image upload failed:', uploadErr);
        }
      }

      updatedProjectDesc = sanitizeProjectDescHtml(updatedProjectDesc);

      const updatePayload: any = {
        project_id: projectId,
        partner,
        title: sanitizeProjectTitle(title).trim().slice(0, 40),
        goal_amount: numeric ? numeric : undefined,
        currency_type: currency,
        project_desc: updatedProjectDesc,
        is_image_display: String(overrideVideoUrl ?? project?.video_url ?? '').trim().length === 0,
        video_url: overrideVideoUrl ?? project?.video_url,
        user_id: project?.user_id,
        user_full_name: project?.user_full_name,
        type: project?.type,
        start_date: project?.start_date,
        end_date: project?.end_date,
        status: project?.status,
      };

      updatePayload.image = imageBase64;
      if (!imageBase64 && targetMediaPreview) {
        updatePayload.image_url = targetMediaPreview;
      }

      await dispatch(updateProject(updatePayload)).unwrap();
      try {
        await dispatch(getProject({ project_id: projectId })).unwrap();
      } catch (refetchErr) {
        console.error('Failed to refetch project after save', refetchErr);
      }
      // eslint-disable-next-line no-alert
      alert(t('common.messages.saved'));
      return true;
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(e?.message || (typeof e === 'string' ? e : t('page.projectSettings.errors.saveFailed')));
      return false;
    }
  };

  const handleOpenStoryModal = () => {
    const rawProjectDesc = String(project?.project_desc ?? '');
    const useLegacyEditorMode = hasLegacyStoryMarkup(rawProjectDesc);
    setIsLegacyStoryEditorMode(useLegacyEditorMode);
    setProjectDesc(useLegacyEditorMode ? prepareStoryHtmlForEditor(rawProjectDesc) : rawProjectDesc);
    setIsStoryModalOpen(true);
  };

  const handleCloseStoryModal = () => {
    setProjectDesc(String(project?.project_desc ?? ''));
    setIsLegacyStoryEditorMode(false);
    setIsStoryModalOpen(false);
  };

  const handleSaveStoryModal = async () => {
    const ok = await handleSave();
    if (ok) setIsStoryModalOpen(false);
  };

  const handleOpenMediaModal = () => {
    const nextMediaType = project?.video_url ? 'video' : project?.image_url ? 'image' : 'none';
    setTempMediaType(nextMediaType);
    setTempMediaPreview(mediaPreview);
    setTempMediaFile(null);
    setTempVideoUrl(project?.video_url ? String(project.video_url) : '');
    setTempVideoPreviewEnabled(Boolean(project?.video_url));
    setIsMediaModalOpen(true);
  };

  const handleSaveMediaModal = async () => {
    if (tempMediaType === 'none') {
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl('');
      await handleSave({ mediaFile: null, videoUrl: '', mediaPreview: null });
    } else if (tempMediaType === 'video') {
      const trimmed = tempVideoUrl.trim();
      const nextVideoUrl = isValidHttpUrl(trimmed) ? trimmed : '';
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl(nextVideoUrl);
      await handleSave({ mediaFile: null, videoUrl: nextVideoUrl, mediaPreview: null });
    } else {
      if (tempMediaFile) {
        setMediaFile(tempMediaFile);
        setMediaPreview(tempMediaPreview);
      }
      setVideoUrl('');
      await handleSave({ mediaFile: tempMediaFile, videoUrl: '' });
    }
    setIsMediaModalOpen(false);
  };

  const fundingLabel = project?.type === 'OT'
    ? t('page.projectManagement.fundingType.oneTime')
    : t('page.projectManagement.fundingType.recurring');
  const createdText = project?.created_at ? String(project.created_at).slice(0, 10).replaceAll('-', '.') : 'YYYY.MM.DD';
  const keymanText = project?.keyman ? t('common.labels.present') : t('common.labels.none');
  const supportPercent =
    typeof (project?.support_project as any)?.support_percent === 'string'
      ? (project?.support_project as any).support_percent
      : '3';
  const supportText = t('page.projectSettings.labels.supporting', { percent: supportPercent });

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isImageSizeAllowed(file)) {
        alert(t('common.errors.imageTooLarge'));
        e.target.value = '';
        return;
      }
      setTempMediaFile(file);
      setTempMediaPreview(URL.createObjectURL(file));
    }
  };

  const buildVideoEmbedUrl = (rawUrl: string) => {
    if (!rawUrl.trim()) return '';
    try {
      const url = new URL(rawUrl.trim());
      const host = url.hostname.replace('www.', '');
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  };

  const videoEmbedUrl = useMemo(() => buildVideoEmbedUrl(videoUrl), [videoUrl]);
  const videoPreviewSrc = useMemo(() => {
    const trimmed = videoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return videoEmbedUrl || trimmed;
  }, [videoEmbedUrl, videoUrl]);
  const tempVideoEmbedUrl = useMemo(() => buildVideoEmbedUrl(tempVideoUrl), [tempVideoUrl]);
  const tempVideoPreviewSrc = useMemo(() => {
    const trimmed = tempVideoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return tempVideoEmbedUrl || trimmed;
  }, [tempVideoEmbedUrl, tempVideoUrl]);
  const showTempVideoUrlError = Boolean(tempVideoUrl.trim()) && !isValidHttpUrl(tempVideoUrl.trim());
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const showGoalPreview = currency === 'KRW' && numericGoal >= 10000;
  const goalPreviewText = showGoalPreview ? formatKoreanCurrency(numericGoal) : '';

  const titleTrimmed = useMemo(() => sanitizeProjectTitle(title).trim(), [title]);
  const minGoal = currency === 'KRW' ? 35000 : 35;
  const goalAmountError = goalAmount.trim() !== '' && numericGoal < minGoal;
  const titleError = titleTrimmed.length > 0 && (titleTrimmed.length < 4 || titleTrimmed.length > 40);
  const canSaveBasic = titleTrimmed.length >= 4 && titleTrimmed.length <= 40 && numericGoal >= minGoal;

  const storyEmbeddedImageCount = useMemo(
    () => (projectDesc.match(/<figure/g) || []).length,
    [projectDesc],
  );
  // Legacy HTML도 그대로 렌더/편집해서 <br>는 줄바꿈으로 보이고, 추가 사진도 넣을 수 있게 처리한다.
  const isLegacyStoryHtml = false;
  const legacyStoryText = useMemo(
    () => projectDesc.replace(/<br\s*\/?>/gi, '\n'),
    [projectDesc],
  );
  const readonlyStoryHtml = useMemo(
    () => (hasLegacyStoryMarkup(projectDesc) ? prepareStoryHtmlForEditor(projectDesc) : projectDesc),
    [projectDesc],
  );
  const isStoryImageLimitReached = storyEmbeddedImageCount >= STORY_IMAGE_LIMIT;

  const handleRequestCloseModal = () => {
    setExitConfirmTarget('media');
  };

  const handleConfirmExit = () => {
    if (exitConfirmTarget === 'media') setIsMediaModalOpen(false);
    setExitConfirmTarget(null);
  };

  const handleCurrencyChange = (next: 'KRW' | 'USD') => {
    if (next === currency) return;
    setCurrency(next);
    setGoalAmountKrw('');
    setGoalAmountUsd('');
  };

  return (
    <WebPageLayout title={t('common.layout.projectManagement')} sidebarActive="settings">
      <Container>
        <ProjectDetailHeader title={headerTitle} projectId={projectId} variant="web" showFullTitle />

        <Wrap>
          <PageShell>
            <PageTitle>{t('page.projectSettings.pageTitle')}</PageTitle>

            {isLoading ? (
              <LoadingWrapper>
                <DSLoading variant="content" />
              </LoadingWrapper>
            ) : (
            <Boxes>
            <TabsBox>
              <TabButton type="button" $active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
                <TabIcon src={activeTab === 'basic' ? searchActiveIcon : searchIcon} alt="" />
                {t('page.projectSettings.tabs.basic')}
              </TabButton>
              <TabButton type="button" $active={activeTab === 'story'} onClick={() => setActiveTab('story')}>
                <TabIcon src={activeTab === 'story' ? penActiveIcon : penIcon} alt="" />
                {t('page.projectSettings.tabs.story')}
              </TabButton>
            </TabsBox>

            {activeTab === 'basic' ? (
              <>
                <PanelCard $paddingBottom={34}>
                  <FieldTitle>{t('page.projectSettings.sections.goalAmount')}</FieldTitle>
                  <CurrencyRow>
                    <CurrencyBtn type="button" $active={currency === 'KRW'} onClick={() => handleCurrencyChange('KRW')}>
                      <RadioDot $active={currency === 'KRW'} /> {t('common.currency.krw')}
                    </CurrencyBtn>
                    <CurrencyBtn type="button" $active={currency === 'USD'} onClick={() => handleCurrencyChange('USD')}>
                      <RadioDot $active={currency === 'USD'} /> {t('common.currency.usd')}
                    </CurrencyBtn>
                  </CurrencyRow>

                  <InputBox $width={392} $error={goalAmountError}>
                    <InputLabel>{t('page.projectSettings.labels.goalAmount')}</InputLabel>
                    <InputField
                      value={goalAmount}
                      onChange={(e) => setGoalAmount(formatGoal(e.target.value, currency))}
                      onKeyDown={handleGoalAmountKeyDown}
                      placeholder={t('page.projectSettings.placeholders.goalAmount')}
                      inputMode="numeric"
                      $error={goalAmountError}
                    />
                  </InputBox>
                  {showGoalPreview ? <GoalPreview>{goalPreviewText}</GoalPreview> : null}

                  <HelperText $error={goalAmountError}>
                    {currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}
                  </HelperText>
                  <WarnRow>
                    <WarnIcon src={infoIconImg} alt="info" />
                    <WarnText>{t('page.projectSettings.helpers.recurringGoal')}</WarnText>
                  </WarnRow>

                  <Divider />

                  <InputBox $error={titleError} style={{ maxWidth: 562 }}>
                    <InputLabel>{t('page.projectSettings.labels.projectTitle')}</InputLabel>
                    <InputField
                      value={title}
                      onChange={(e) => setTitle(sanitizeProjectTitle(e.target.value).slice(0, 40))}
                      placeholder={t('page.projectSettings.placeholders.projectTitle')}
                      $error={titleError}
                    />
                  </InputBox>
                  <HelperText $error={titleError}>{t('page.projectSettings.helpers.titleLength')}</HelperText>
                  <Divider $fullWidth $gapAbove={34} $gapBelow={16} />
                  <ButtonRow $noMarginTop $paddingRight={13}>
                    <CancelSaveButtons
                      onCancel={handleCancel}
                      onSave={() => handleSave()}
                      saving={isSaving}
                      disabled={!projectId || !canSaveBasic}
                      platform="web"
                    />
                  </ButtonRow>
                </PanelCard>

                <PanelCard>
                  <InfoRow>
                    <InfoLabel>{t('page.projectSettings.info.fundingType')}</InfoLabel>
                    <InfoValue>{fundingLabel}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>{t('page.projectSettings.info.createdAt')}</InfoLabel>
                    <InfoValue>{createdText}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>{t('page.projectSettings.info.keyman')}</InfoLabel>
                    <InfoValue>{keymanText}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>{t('page.projectSettings.info.missionFund')}</InfoLabel>
                    <InfoValue>{supportText}</InfoValue>
                  </InfoRow>
                </PanelCard>
              </>
            ) : (
              <>
                <MediaCardWrapper>
                  <MediaHeaderCard>
                    <CardHeaderRow>
                      <CardTitle>{t('page.projectSettings.sections.media')}</CardTitle>
                      <EditButton type="button" onClick={handleOpenMediaModal}>
                        {t('page.projectSettings.actions.edit')}
                      </EditButton>
                    </CardHeaderRow>
                  </MediaHeaderCard>
                  <MediaContentCard>
                    <MediaBox $hasImage={!videoPreviewSrc && Boolean(mediaPreview)}>
                      {videoPreviewSrc ? (
                        <MediaIframe
                          src={videoPreviewSrc}
                          title="video preview"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : mediaPreview ? (
                        <MediaImg src={getCloudFrontImageUrl(mediaPreview, THUMBNAIL_DIMENSIONS) || mediaPreview} alt="" />
                      ) : (
                        <EmptyText>{t('page.postManagement.modal.mediaType.none')}</EmptyText>
                      )}
                      {(videoPreviewSrc || mediaPreview) ? <Badge><BadgeIcon src={checkRoundedIcon} alt="" />{t('common.labels.main')}</Badge> : null}
                    </MediaBox>
                  </MediaContentCard>
                </MediaCardWrapper>

                <MediaCardWrapper>
                  <MediaHeaderCard>
                    <CardHeaderRow>
                      <CardTitle>{t('page.projectSettings.sections.story')}</CardTitle>
                      <EditButton type="button" onClick={handleOpenStoryModal}>
                        {t('page.projectSettings.actions.edit')}
                      </EditButton>
                    </CardHeaderRow>
                  </MediaHeaderCard>
                  <StoryContentCard>
                    <StoryInlineWrap>
                      {projectDesc ? (
                        <SafeHtmlViewer html={readonlyStoryHtml} />
                      ) : (
                        <EmptyText>{t('page.projectSettings.empty.story')}</EmptyText>
                      )}
                    </StoryInlineWrap>
                  </StoryContentCard>
                </MediaCardWrapper>
              </>
            )}
          </Boxes>
            )}
          </PageShell>
        </Wrap>
      </Container>

      <DSModal
        open={Boolean(exitConfirmTarget)}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmTarget(null)}
        actions={[
          { label: t('common.exitConfirm.leave'), variant: 'secondary', onClick: handleConfirmExit },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmTarget(null) },
        ]}
      />

      {/* Media Edit Modal */}
      {isMediaModalOpen && (
        <ModalOverlay onClick={() => setIsMediaModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('page.projectSettings.modal.editTitle')}</ModalTitle>
              <CloseBtn onClick={handleRequestCloseModal}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <SectionDesc>{t('page.postManagement.modal.mediaSectionTitle')}</SectionDesc>
              <RadioRow>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'image'}
                    onChange={() => setTempMediaType('image')}
                  />
                  {t('page.postManagement.modal.mediaType.image')}
                </RadioItem>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'video'}
                    onChange={() => setTempMediaType('video')}
                  />
                  {t('page.postManagement.modal.mediaType.video')}
                </RadioItem>
              </RadioRow>

              {tempMediaType === 'image' && (
                <FileRow>
                  <FileNameBox>{tempMediaFile?.name || t('page.postManagement.modal.uploadHintImage')}</FileNameBox>
                  <AttachBtn type="button" onClick={() => openFilePicker(mediaInputRef.current)}>
                    {t('common.actions.attach')}
                  </AttachBtn>
                  <input
                    id="modal-media-upload"
                    type="file"
                    accept="image/*"
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                    onChange={handleMediaChange}
                    ref={mediaInputRef}
                  />
                </FileRow>
              )}

              {tempMediaType === 'video' && (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showTempVideoUrlError ? 'error' : tempVideoUrlFocused ? 'focus' : tempVideoUrl.trim() ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(tempVideoUrlFocused || tempVideoUrl.trim()) && <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>}
                        <VideoUrlInput
                          placeholder={tempVideoUrlFocused || tempVideoUrl.trim() ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={tempVideoUrl}
                          onChange={(e) => {
                            setTempVideoUrl(e.target.value);
                            setTempVideoPreviewEnabled(true);
                          }}
                          onFocus={() => setTempVideoUrlFocused(true)}
                          onBlur={() => setTempVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {tempVideoUrl.trim() ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setTempVideoUrl('');
                            setTempVideoPreviewEnabled(false);
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showTempVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                </>
              )}

              <PreviewArea>
                {tempMediaType === 'none' ? (
                  <MediaPreviewEmptyWrap>
                    <MediaPreviewEmpty>{t('page.projectSettings.media.empty')}</MediaPreviewEmpty>
                  </MediaPreviewEmptyWrap>
                ) : tempMediaType === 'video' && tempVideoPreviewEnabled && tempVideoPreviewSrc ? (
                  <PreviewIframe
                    src={tempVideoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : tempMediaType === 'video' ? (
                  <MediaPreviewEmptyWrap>
                    <MediaPreviewEmpty>{t('page.projectSettings.media.linkPreviewHint')}</MediaPreviewEmpty>
                    <MediaPreviewVideoIcon
                      xmlns="http://www.w3.org/2000/svg"
                      width="70"
                      height="70"
                      viewBox="0 0 70 70"
                      fill="none"
                      aria-hidden
                      focusable="false"
                    >
                      <path d="M29.1665 24.7915L46.6665 34.9998L29.1665 45.2082V24.7915Z" fill="#BCBCBC" />
                      <path
                        d="M35 14.5835C61.25 14.5835 61.25 14.5835 61.25 35.0002C61.25 55.4168 61.25 55.4168 35 55.4168C8.75 55.4168 8.75 55.4168 8.75 35.0002C8.75 14.5835 8.75 14.5835 35 14.5835Z"
                        stroke="#BCBCBC"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </MediaPreviewVideoIcon>
                  </MediaPreviewEmptyWrap>
                ) : tempMediaPreview ? (
                  <>
                    <PreviewImg src={tempMediaPreview} alt="" />
                    <Badge>
                      <BadgeIcon src={checkRoundedIcon} alt="" />
                      {t('common.labels.main')}
                    </Badge>
                  </>
                ) : (
                  <MediaPreviewEmptyWrap>
                    <MediaPreviewEmptyText>{t('page.postManagement.modal.previewHintImage')}</MediaPreviewEmptyText>
                    <MediaPreviewIcon src={pictureIconImg} alt="" />
                  </MediaPreviewEmptyWrap>
                )}
              </PreviewArea>
            </ModalBody>
            <ModalFooter>
              <CancelSaveButtons
                onCancel={() => setIsMediaModalOpen(false)}
                onSave={handleSaveMediaModal}
                saving={isSaving}
                disabled={isSaving}
                platform="web"
              />
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {isStoryModalOpen && (
        <StoryModalOverlay onClick={handleCloseStoryModal}>
          <StoryModalContent onClick={(e) => e.stopPropagation()}>
            <StoryModalHeader>
              <StoryModalTitle>{t('page.projectSettings.modal.editTitle')}</StoryModalTitle>
              <StoryModalCloseButton onClick={handleCloseStoryModal}>✕</StoryModalCloseButton>
            </StoryModalHeader>
            <StoryModalStep2Container>
              <StoryModalStep2Header>
                <StoryModalStep2Guide>
                  {`${t('page.projectSettings.modal.storyDescLine1')}\n${t('page.projectSettings.modal.storyDescLine2')}`}
                </StoryModalStep2Guide>
              </StoryModalStep2Header>
              <StoryModalStep2Scroll ref={storyEditorScrollRef}>
                {isLegacyStoryHtml ? (
                  <StoryRawHtmlEditor
                    ref={legacyStoryEditorRef}
                    value={projectDesc}
                    onChange={(e) => setProjectDesc(e.target.value)}
                    spellCheck={false}
                  />
                ) : (
                  <RichTextEditor
                    ref={storyEditorRef}
                    value={projectDesc}
                    onChange={(html) => setProjectDesc(html)}
                    placeholder={t('page.projectSettings.modal.storyPlaceholder')}
                    pictureIconSrc="/Image/posts/photoIcon.png"
                    maxImages={STORY_IMAGE_LIMIT}
                    imageCount={storyEmbeddedImageCount}
                    showToolbar={false}
                    onFocus={() => {
                      requestAnimationFrame(() => {
                        scrollStoryEditorIntoView();
                      });
                    }}
                    onImageInserted={() => {
                      requestAnimationFrame(() => {
                        storyEditorRef.current?.focusAtEnd();
                        requestAnimationFrame(() => {
                          scrollStoryEditorIntoView();
                          requestAnimationFrame(() => {
                            storyEditorRef.current?.focusAtEnd();
                          });
                        });
                      });
                    }}
                  />
                )}
              </StoryModalStep2Scroll>
              {!isLegacyStoryHtml ? (
                <StoryModalAddPhotoWrap>
                  <StoryAddPhotoBtn
                    type="button"
                    onClick={() => storyEditorRef.current?.openImagePicker()}
                    disabled={isStoryImageLimitReached}
                  >
                    <StoryAddPhotoLabel>
                      <img
                        src="/Image/posts/photoIcon.png"
                        alt={t('page.postManagement.modal.photoAlt')}
                        style={{ width: '28px', height: '28px', marginRight: '8px' }}
                      />
                      {t('page.postManagement.modal.addPhoto', { count: storyEmbeddedImageCount })}
                    </StoryAddPhotoLabel>
                  </StoryAddPhotoBtn>
                </StoryModalAddPhotoWrap>
              ) : null}
            </StoryModalStep2Container>
            <StoryModalFooter>
              <CancelSaveButtons
                onCancel={handleCloseStoryModal}
                onSave={handleSaveStoryModal}
                saving={isSaving}
                disabled={!projectId || !canSaveBasic}
                platform="web"
              />
            </StoryModalFooter>
          </StoryModalContent>
        </StoryModalOverlay>
      )}

    </WebPageLayout>
  );
};

export default ProjectSettingsWeb;

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-right: 0;
  padding-bottom: 196px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: ${convertToVwMob(24)} 0;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 920px;
`;

const PageShell = styled.div`
  width: 100%;

  @media (max-width: ${FOOTER_BREAKPOINT}px) {
    padding: 12px;
  }
`;

const Boxes = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const PageTitle = styled.h1`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: #000000;
  margin: 0;
`;

const TabsBox = styled.div`
  display: flex;
  height: 58px;
  border-radius: 10px 10px 0 0;
  border: 1px solid #eee;
  background: #fff;
  overflow: hidden;
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  padding: 18px 22px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  text-align: center;
${({ theme }) => theme.typography.contents.medium20}

  /* 스크린샷처럼 활성 탭 하단 라인 */
  &::after {
    content: '';
    position: absolute;
    left: 18px;
    right: 18px;
    bottom: 0;
    height: 3px;
    border-radius: 3px;
    background: ${({ $active }) => ($active ? '#2BC2E1' : 'transparent')};
  }
`;

const TabIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const PanelCard = styled.div<{ $paddingBottom?: number }>`
  border-radius: 10px;
  border: 1px solid #eee;
  background: #fff;
  padding: 18px;
  ${({ $paddingBottom }) =>
    $paddingBottom !== undefined &&
    `
    padding-bottom: ${$paddingBottom}px;
  `}
`;

const MediaCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MediaHeaderCard = styled.div`
  border-radius: 12px 12px 0 0;
  border: 1px solid #eee;
  border-bottom: 1px solid #eee;
  background: #fff;
  padding: 20px 34px;
`;

const MediaContentCard = styled.div`
  border-radius: 0 0 10px 10px;
  border: 1px solid #eee;
  border-top: none;
  background: #fff;
  padding: 20px 34px;
  display: flex;
  justify-content: center;
`;

const StoryContentCard = styled.div`
  border-radius: 0 0 10px 10px;
  border: 1px solid #eee;
  border-top: none;
  background: #fff;
`;

const StoryInlineWrap = styled.div`
  padding: 20px 34px 28px;
  box-sizing: border-box;
`;

const FieldTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const CurrencyRow = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 18px;
  align-items: center;
`;

const CurrencyBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const RadioDot = styled.span<{ $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;  

const InputBox = styled.div<{ $width?: number; $height?: number; $error?: boolean }>`
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
  ${({ $height }) => $height && `height: ${$height}px;`}
  box-sizing: border-box;
  margin-top: 12px;
  border-radius: 4px;
  border: ${({ $error }) => ($error ? '1.6px solid #ff0000' : '1px solid #eee')};
  background: #fff;
  padding: 8px 12px;
  min-height: 84px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
  }

  &:focus-within {
    border-color: ${({ $error }) => ($error ? '#ff0000' : '#2bc2e1')};
    box-shadow: ${({ $error }) =>
      $error ? '0 0 0 2px rgba(255, 0, 0, 0.25)' : '0 0 0 2px rgba(43, 194, 225, 0.2)'};
  }
`;

const InputLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const InputField = styled.input<{ $error?: boolean }>`
  margin-top: 6px;
  width: 100%;
  border: none;
  outline: none;

  color: ${({ $error }) => ($error ? '#ff0000' : '#17171b')};
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const GoalPreview = styled.div`
  margin-top: 8px;
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
`;

const HelperText = styled.div<{ $error?: boolean }>`
  margin-top: 8px;
  color: ${({ $error }) => ($error ? '#ff0000' : theme.colors.primaryBlue[70])};
  ${theme.typography.contents.regular16}
`;

const WarnRow = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const WarnIcon = styled.img`
  width: 18px;
  height: 18px;
  margin-top: 2px;
`;

const WarnText = styled.div`
  color: #ff9500;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const Divider = styled.div<{ $fullWidth?: boolean; $gapAbove?: number; $gapBelow?: number }>`
  margin: ${({ $gapAbove }) => ($gapAbove !== undefined ? $gapAbove : 18)}px 0 ${({ $gapBelow }) => ($gapBelow !== undefined ? $gapBelow : 18)}px 0;
  height: 1px;
  background: #EEEEEE;
  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    margin-left: -18px;
    margin-right: -18px;
    width: calc(100% + 36px);
  `}
`;

const ButtonRow = styled.div<{ $noMarginTop?: boolean; $paddingRight?: number }>`
  margin-top: ${({ $noMarginTop }) => ($noMarginTop ? 0 : 18)}px;
  padding-right: ${({ $paddingRight }) => ($paddingRight !== undefined ? $paddingRight : 0)}px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const InfoLabel = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const InfoValue = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTitle = styled.div`
  color: #17171b;
${({ theme }) => theme.typography.contents.medium24}
`;

const EditButton = styled.button`
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.primaryBlue[30]};
  background: #fff;
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
 ${({ theme }) => theme.typography.button.medium20}
  padding: 4px 24px;
  cursor: pointer;
`;

const MediaBox = styled.div<{ $hasImage?: boolean }>`
  margin-top: 12px;
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  width: 562px;
  height: ${({ $hasImage }) => ($hasImage ? 'auto' : '220px')};
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'flex')};
  align-items: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  justify-content: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  position: relative;
  overflow: hidden;
`;

const MediaImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const BadgeIcon = styled.img`
  width: 19px;
  height: 19px;
  object-fit: contain;
`;

const Badge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: ${({ theme }) => theme.colors.primaryBlue[40]};
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px;
${({ theme }) => theme.typography.contents.regular16}
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 1;
`;

const MediaIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const StoryBox = styled.div`
  border-radius: 10px;
  background: #fff;
  padding: 20px 34px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 760px;
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 32px 30px 20px;
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 24px;
  font-weight: 600;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: 24px;
  color: #bcbcbc;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 24px 99px;
  flex: 1;
  overflow-y: auto;
  max-height: 60vh;
`;

const StoryEditorScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-right: -8px;
  padding-right: 8px;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grayScale[10]};
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grayScale[40]};
    border-radius: 4px;
  }
`;

const StoryAddPhotoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 353px;
  max-width: 100%;
  height: 54px;
  box-sizing: border-box;
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  ${({ theme }) => theme.typography.button.medium20}
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  cursor: pointer;
  padding: 0 16px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[15]};
    border-color: ${({ theme }) => theme.colors.primaryBlue[50]};
    color: ${({ theme }) => theme.colors.primaryBlue[50]};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[20]};
    border-color: ${({ theme }) => theme.colors.primaryBlue[60]};
    color: ${({ theme }) => theme.colors.primaryBlue[60]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StoryAddPhotoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const StoryModalStep2Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const StoryModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  overscroll-behavior: none;
`;

const StoryModalContent = styled.div`
  width: 760px;
  max-width: 100%;
  max-height: min(834px, calc(100vh - 32px));
  margin: 0;
  flex-shrink: 1;
  min-height: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  overscroll-behavior: contain;
`;

const StoryModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 30px 20px 30px;
  background: #f8f8f8;
  flex-shrink: 0;
`;

const StoryModalTitle = styled.h2`
  ${({ theme }) => theme.typography.head.semiBold24}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const StoryModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  cursor: pointer;
  padding: 0;
  display: flex;
`;

const StoryModalStep2Header = styled.div`
  padding: 24px 99px 1px;
  flex-shrink: 0;

  @media (max-width: 1366px) {
    padding: 24px 99px 1px;
  }

  @media (max-width: 800px) {
    padding: 24px 24px 6px;
  }
`;

const StoryModalStep2Guide = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  margin: 0 0 4px 0;
  white-space: pre-line;

  @media (max-width: 1366px) {
    margin-bottom: 6px;
  }
`;

const StoryModalStep2Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 99px;

  @media (max-width: 800px) {
    padding: 0 24px;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grayScale[10]};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.grayScale[40]};
    border-radius: 4px;
  }
`;

const StoryModalAddPhotoWrap = styled.div`
  padding: 20px 0;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
`;

const SectionDesc = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const RadioRow = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 20px;
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: 20px;
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: 1.5px solid #E5E5E5;
  border-radius: 50%;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #2bc2e1;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease;
  }

  &:checked::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const FileRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const FileNameBox = styled.div`
  flex: 1;
  height: 50px;
  background: #f8f8f8;
  border-radius: 4px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
`;

const AttachBtn = styled.button`
  width: 130px;
  height: 50px;
  background: #fff;
  border: 1px solid #bcbcbc;
  border-radius: 4px;
  color: #676767;
  font-family: Pretendard;
  font-size: 20px;
  cursor: pointer;

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;

/** 새 프로젝트 생성(NewProject web) Step2 MediaPreview와 동일 */
const PreviewArea = styled.div`
  margin-top: 16px;
  width: 100%;
  height: 240px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  background: #f0f1f2;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
`;

const MediaPreviewEmptyWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  padding: 12px;
`;

const MediaPreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MediaPreviewEmptyText = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const MediaPreviewIcon = styled.img`
  width: 74px;
  height: 74px;
  object-fit: contain;
  opacity: 0.9;
`;

const MediaPreviewVideoIcon = styled.svg`
  width: 70px;
  height: 70px;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalFooter = styled.div`
  padding: 24px 40px 32px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const StoryModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 31px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  flex-shrink: 0;

  @media (max-width: 800px) {
    padding: 16px 24px 24px;
  }
`;

const VideoUrlRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 16px;
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: 6px;
  border: 2px solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-bottom: 8px;
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: 50px;
  width: 130px;
  border-radius: 4px;
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;

const EmptyText = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
`;

const StoryRawHtml = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const StoryRawHtmlEditor = styled.textarea`
  width: 100%;
  min-height: 360px;
  border: none;
  outline: none;
  resize: vertical;
  background: transparent;
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;
